import React, { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import { Card, Tabs, Table, Tag, Button, Space, message, Form, Row, Col, InputNumber, Select, DatePicker, Input, Typography, Divider } from 'antd';
import { SwapOutlined, SlidersOutlined, AuditOutlined, DeleteOutlined, BarcodeOutlined } from '@ant-design/icons';
import RightDrawer from '../components/common/RightDrawer';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import useStore from '../store/useStore';
import { productsAPI, locationsAPI } from '../services/api';

const Stock = () => {
  const { products, setProducts, locations, setLocations } = useStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Drawers
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerType, setDrawerType] = useState(null); // 'transfer' | 'adjustment' | 'count'
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [form] = Form.useForm();
  const [transferItems, setTransferItems] = useState([]);
  const [productPickerOpen, setProductPickerOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [prodRes, locRes] = await Promise.all([
        productsAPI.getAll(),
        locationsAPI.getAll?.() || Promise.resolve({ data: [] }),
      ]);
      setProducts(prodRes.data || []);
      setLocations(locRes.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [setProducts, setLocations]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDrawer = (type) => {
    setDrawerType(type);
    form.resetFields();
    setDrawerVisible(true);
    if (type === 'transfer') {
      setTransferItems([]);
      // seed default date
      form.setFieldsValue({ transferDate: dayjs() });
    }
  };
  const closeDrawer = () => {
    setDrawerVisible(false);
    setDrawerType(null);
    form.resetFields();
    setTransferItems([]);
  };
  const onSubmitDrawer = async () => {
    try {
      setDrawerLoading(true);
      const values = await form.validateFields();
      const payload = {
        type: drawerType,
        ...values,
        items: drawerType === 'transfer' ? transferItems : undefined,
      };
      console.log('Submitting stock action (mock):', payload);
      message.success(`${drawerType === 'transfer' ? 'Transfer' : drawerType === 'adjustment' ? 'Adjustment' : 'Count'} saved (mock)`);
      closeDrawer();
    } catch (e) {
      // validation errors handled by antd
    } finally {
      setDrawerLoading(false);
    }
  };

  if (loading) return <LoadingSpinner size="large" tip="Loading stock..." />;
  if (error) return <ErrorDisplay error={error} onRetry={fetchData} title="Failed to load stock" />;

  const stockColumns = [
    {
      title: 'Product',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
    },
    {
      title: 'Current Stock',
      dataIndex: 'currentStock',
      key: 'currentStock',
    },
    {
      title: 'Reorder Point',
      dataIndex: 'reorderPoint',
      key: 'reorderPoint',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, r) => {
        const status = r.currentStock <= 0 ? { color: 'red', text: 'Out of Stock' } : r.currentStock <= (r.reorderPoint || 0) ? { color: 'orange', text: 'Low Stock' } : { color: 'green', text: 'In Stock' };
        return <Tag color={status.color}>{status.text}</Tag>;
      }
    }
  ];

  const renderTransferItemsTable = () => {
    const columns = [
      { title: 'Product', dataIndex: 'name', key: 'name', render: (_, r) => (
        <div>
          <div className="font-medium">{r.name}</div>
          <div className="text-gray-500 text-xs">{r.sku}</div>
        </div>
      ) },
      { title: 'Quantity', dataIndex: 'quantity', key: 'quantity', width: 140, render: (v, r, idx) => (
        <InputNumber min={1} className="w-full" value={v} onChange={(val) => {
          const next = [...transferItems];
          next[idx] = { ...next[idx], quantity: val || 1 };
          setTransferItems(next);
        }} />
      ) },
      { title: 'From sublocation', dataIndex: 'fromSub', key: 'fromSub', width: 200, render: (v, r, idx) => (
        <Select
          placeholder="Select"
          value={v}
          onChange={(val) => {
            const next = [...transferItems];
            next[idx] = { ...next[idx], fromSub: val };
            setTransferItems(next);
          }}
          options={[{ value: 'Main', label: 'Main' }, { value: 'A1', label: 'A1' }, { value: 'A2', label: 'A2' }]}
        />
      ) },
      { title: 'To sublocation', dataIndex: 'toSub', key: 'toSub', width: 200, render: (v, r, idx) => (
        <Select
          placeholder="Select"
          value={v}
          onChange={(val) => {
            const next = [...transferItems];
            next[idx] = { ...next[idx], toSub: val };
            setTransferItems(next);
          }}
          options={[{ value: 'Main', label: 'Main' }, { value: 'B1', label: 'B1' }, { value: 'B2', label: 'B2' }]}
        />
      ) },
      { title: '', key: 'actions', width: 60, render: (_, __, idx) => (
        <Button danger type="text" icon={<DeleteOutlined />} onClick={() => {
          const next = transferItems.filter((_, i) => i !== idx);
          setTransferItems(next);
        }} />
      ) },
    ];
    return (
      <Table
        dataSource={transferItems}
        columns={columns}
        rowKey={(r) => r.id}
        pagination={false}
      />
    );
  };

  const addProductsToTransfer = (ids) => {
    const map = new Map(transferItems.map(i => [i.id, i]));
    ids.forEach((id) => {
      if (!map.has(id)) {
        const p = (products || []).find(x => x.id === id);
        if (p) map.set(id, { id, name: p.name, sku: p.sku, quantity: 1, fromSub: undefined, toSub: undefined });
      }
    });
    setTransferItems(Array.from(map.values()));
  };

  const renderDrawerForm = () => (
    <RightDrawer
      title={drawerType === 'transfer' ? 'New Stock Transfer' : drawerType === 'adjustment' ? 'New Stock Adjustment' : 'New Stock Count'}
      open={drawerVisible}
      onClose={closeDrawer}
      loading={drawerLoading}
      width={720}
      footer={drawerType === 'transfer' ? (
        <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200">
          <div className="text-gray-500">
            <Button type="link" danger onClick={() => message.info('Cancel transfer (mock)')}>Cancel transfer</Button>
          </div>
          <div className="flex gap-2">
            <Button onClick={closeDrawer}>Close</Button>
            <Button type="primary" htmlType="submit" form="drawer-form">Send transfer</Button>
          </div>
        </div>
      ) : undefined}
    >
      <Form layout="vertical" form={form} onFinish={onSubmitDrawer} id="drawer-form">
        {drawerType === 'transfer' ? (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Typography.Text type="secondary">Open</Typography.Text>
                <div className="text-xl font-semibold">{`ST-${String(Date.now()).slice(-6)}`}</div>
              </Col>
              <Col span={12}>
                <div className="flex justify-end gap-2">
                  <Button type="text" onClick={() => message.info('Attachments (mock)')}>Attachments</Button>
                  <Button type="text" onClick={() => message.info('Copy (mock)')}>Copy</Button>
                  <Button type="text" onClick={() => window.print()}>Print</Button>
                  <Button type="text" onClick={() => message.info('Email (mock)')}>Email</Button>
                </div>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="fromLocationId" label="From location" rules={[{ required: true }]}>
                  <Select placeholder="Select" options={(locations || []).map(l => ({ value: l.id, label: l.name }))} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="toLocationId" label="To location" rules={[{ required: true }]}>
                  <Select placeholder="Select" options={(locations || []).map(l => ({ value: l.id, label: l.name }))} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="assignedTo" label="Assigned to">
                  <Select placeholder="Select assignee" options={[{ value: 'john', label: 'John Doe' }, { value: 'jane', label: 'Jane Smith' }]} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="transferDate" label="Date" rules={[{ required: true }]}>
                  <DatePicker className="w-full" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="remarks" label="Remarks">
              <Input.TextArea rows={3} placeholder="Notes for this transfer" />
            </Form.Item>

            <Divider />
            <div className="flex justify-between items-center mb-3">
              <Typography.Text strong>Items</Typography.Text>
              <Space>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Add products"
                  style={{ minWidth: 260 }}
                  open={productPickerOpen}
                  onDropdownVisibleChange={setProductPickerOpen}
                  onChange={(ids) => addProductsToTransfer(ids)}
                  options={(products || []).map(p => ({ value: p.id, label: `${p.name} (${p.sku})` }))}
                />
                <Button icon={<BarcodeOutlined />} onClick={() => message.info('Scan to add (mock)')}>Scan to add</Button>
              </Space>
            </div>
            {renderTransferItemsTable()}
          </>
        ) : (
          <Row gutter={16}>
            {(drawerType === 'adjustment' || drawerType === 'count') && (
              <>
                <Col span={12}>
                  <Form.Item name="productId" label="Product" rules={[{ required: true }]}>
                    <Select placeholder="Select product" showSearch optionFilterProp="label" options={(products || []).map(p => ({ value: p.id, label: `${p.name} (${p.sku})` }))} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="quantity" label="Quantity" rules={[{ required: true }]}>
                    <InputNumber className="w-full" min={0} />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        )}
      </Form>
    </RightDrawer>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">Stock</h1>
          <p className="text-gray-600 dark:text-slate-300">Manage inventory stock and movements</p>
        </div>
      </div>

      <Card>
        <Tabs
          defaultActiveKey="current"
          items={[
            {
              key: 'current',
              label: 'Current stock',
              children: (
                <>
                  <div className="flex justify-end mb-4">
                    <Space>
                      <Button icon={<SwapOutlined />} onClick={() => openDrawer('transfer')}>New Transfer</Button>
                      <Button icon={<SlidersOutlined />} onClick={() => openDrawer('adjustment')}>New Adjustment</Button>
                      <Button type="primary" icon={<AuditOutlined />} onClick={() => openDrawer('count')}>New Count</Button>
                    </Space>
                  </div>
                  <Table
                    dataSource={products}
                    columns={stockColumns}
                    rowKey="id"
                    pagination={{ showSizeChanger: true, showQuickJumper: true }}
                  />
                </>
              )
            },
            {
              key: 'transfer',
              label: 'Stock transfer',
              children: (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <div className="text-gray-700 dark:text-slate-300">Create and view stock transfers.</div>
                    <Button type="primary" icon={<SwapOutlined />} onClick={() => openDrawer('transfer')}>New Transfer</Button>
                  </div>
                  <Table
                    dataSource={[]}
                    columns={[
                      { title: 'Transfer #', dataIndex: 'number', key: 'number' },
                      { title: 'From', dataIndex: 'from', key: 'from' },
                      { title: 'To', dataIndex: 'to', key: 'to' },
                      { title: 'Date', dataIndex: 'date', key: 'date' },
                      { title: 'Status', dataIndex: 'status', key: 'status' },
                    ]}
                    rowKey={(r, idx) => idx}
                    locale={{ emptyText: 'No transfers yet. Click New Transfer to create one.' }}
                    pagination={false}
                  />
                </>
              )
            },
            {
              key: 'adjustment',
              label: 'Stock adjustment',
              children: (
                <div className="text-gray-700 dark:text-slate-300">
                  Use the New Adjustment button to correct stock levels. (Mock view)
                </div>
              )
            },
            {
              key: 'counts',
              label: 'Stock counts',
              children: (
                <div className="text-gray-700 dark:text-slate-300">
                  Schedule and record cycle counts. (Mock view)
                </div>
              )
            },
          ]}
        />
      </Card>

      {renderDrawerForm()}
    </div>
  );
};

export default Stock;
